import React, { useState, useEffect } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Card } from './ui/card';
import stripePromise from '../services/stripe';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1B1B1B',
      '::placeholder': {
        color: '#888680',
      },
      padding: '16px',
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  },
  hidePostalCode: false
};

const PaymentForm = ({ formData, onPaymentSuccess, onPaymentError, loading, setLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });

  useEffect(() => {
    // Create payment intent when component loads
    createPaymentIntent();
  }, []);

  const createPaymentIntent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_type: formData.selectedPlan,
          customer_email: formData.email,
          customer_details: {
            first_name: formData.fullName.split(' ')[0],
            last_name: formData.fullName.split(' ').slice(1).join(' '),
            company: formData.companyName,
            phone: formData.phone
          }
        })
      });

      const data = await response.json();
      
      if (data.client_secret) {
        setClientSecret(data.client_secret);
      } else {
        throw new Error('Failed to create payment intent');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      onPaymentError('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setProcessingPayment(true);
    setPaymentError('');

    const cardElement = elements.getElement(CardNumberElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            address: {
              line1: billingAddress.line1,
              line2: billingAddress.line2,
              city: billingAddress.city,
              state: billingAddress.state,
              postal_code: billingAddress.postal_code,
              country: billingAddress.country
            }
          }
        }
      });

      if (error) {
        console.error('Payment failed:', error);
        setPaymentError(error.message);
        onPaymentError(error.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent);
        
        // Confirm payment on backend
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            payment_intent_id: paymentIntent.id
          })
        });

        onPaymentSuccess({
          paymentIntentId: paymentIntent.id,
          amount: paymentIntent.amount,
          status: paymentIntent.status
        });
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentError('An unexpected error occurred. Please try again.');
      onPaymentError('An unexpected error occurred. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getSelectedPlan = () => {
    const plans = {
      'investment': { title: 'Investment Build', price: '$22,500', total: '$22,500' },
      'milestone': { title: 'Milestone Build', price: '$12,500 × 2', total: '$25,000' },
      'installment': { title: 'Installment Build', price: '$7,000 × 4', total: '$28,000' },
      'diagnostic': { title: 'Strategic Diagnostic', price: '$5,000', total: '$5,000' },
      'acceleration': { title: 'Acceleration+', price: '$38,000', total: '$38,000' }
    };
    return plans[formData.selectedPlan] || plans['investment'];
  };

  const getTotalDueToday = () => {
    switch (formData.selectedPlan) {
      case 'investment': return '$22,500';
      case 'milestone': return '$12,500';
      case 'installment': return '$7,000';
      case 'diagnostic': return '$5,000';
      case 'acceleration': return '$38,000';
      default: return '$22,500';
    }
  };

  const plan = getSelectedPlan();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="headline-medium text-charcoal mb-4">Secure Payment</h2>
        <p className="body-medium text-graphite">Complete your enrollment with secure payment processing</p>
      </div>

      <Card className="editorial-card p-8">
        {/* Order Summary */}
        <div className="mb-8">
          <h3 className="subhead-medium text-charcoal mb-6 text-center">Order Summary</h3>
          <div className="bg-ivory p-6 rounded-lg space-y-4">
            <div className="flex justify-between items-center">
              <span className="body-medium text-charcoal">Plan:</span>
              <span className="body-medium text-bronze font-semibold">{plan.title}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="body-medium text-charcoal">Investment:</span>
              <span className="body-medium text-charcoal">{plan.price}</span>
            </div>
            <div className="border-t border-bronze-light pt-4">
              <div className="flex justify-between items-center">
                <span className="subhead-medium text-charcoal">Total Due Today:</span>
                <span className="text-2xl font-bold text-bronze">{getTotalDueToday()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="mb-8">
          <h3 className="subhead-medium text-charcoal mb-6">Billing Address</h3>
          <div className="space-y-4">
            <div>
              <label className="label-text text-charcoal block mb-2">ADDRESS LINE 1 *</label>
              <input
                type="text"
                value={billingAddress.line1}
                onChange={(e) => setBillingAddress({...billingAddress, line1: e.target.value})}
                className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                placeholder="123 Main Street"
                required
              />
            </div>
            
            <div>
              <label className="label-text text-charcoal block mb-2">ADDRESS LINE 2</label>
              <input
                type="text"
                value={billingAddress.line2}
                onChange={(e) => setBillingAddress({...billingAddress, line2: e.target.value})}
                className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                placeholder="Apt, Suite, Unit (optional)"
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label-text text-charcoal block mb-2">CITY *</label>
                <input
                  type="text"
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="Los Angeles"
                  required
                />
              </div>
              
              <div>
                <label className="label-text text-charcoal block mb-2">STATE *</label>
                <input
                  type="text"
                  value={billingAddress.state}
                  onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="CA"
                  required
                />
              </div>
              
              <div>
                <label className="label-text text-charcoal block mb-2">ZIP CODE *</label>
                <input
                  type="text"
                  value={billingAddress.postal_code}
                  onChange={(e) => setBillingAddress({...billingAddress, postal_code: e.target.value})}
                  className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                  placeholder="90210"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        {clientSecret ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <h3 className="subhead-medium text-charcoal mb-6">Payment Information</h3>
            <div className="space-y-4">
              <div>
                <label className="label-text text-charcoal block mb-2">CARD NUMBER</label>
                <div className="p-4 border border-bronze-light rounded-lg bg-white">
                  <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text text-charcoal block mb-2">EXPIRY DATE</label>
                  <div className="p-4 border border-bronze-light rounded-lg bg-white">
                    <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                </div>
                <div>
                  <label className="label-text text-charcoal block mb-2">CVC</label>
                  <div className="p-4 border border-bronze-light rounded-lg bg-white">
                    <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                </div>
              </div>
            </div>

            {paymentError && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-red-600 text-center">{paymentError}</p>
              </div>
            )}

            <div className="space-y-4">
              <Button
                type="submit"
                className="btn-primary w-full text-lg py-4"
                disabled={!stripe || processingPayment || loading}
              >
                {processingPayment ? 'Processing Payment...' : `Pay ${getTotalDueToday()}`}
              </Button>

              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-4 h-4 text-bronze mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span className="body-small text-graphite">Secured by 256-bit SSL encryption</span>
                </div>
                <p className="body-small text-graphite">
                  By proceeding, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </form>
        ) : (
          <div className="text-center py-8">
            <div className="text-bronze text-lg mb-2">Initializing secure payment...</div>
            <div className="w-8 h-8 border-2 border-bronze border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        )}
      </Card>
    </div>
  );
};

const StripePayment = ({ formData, onPaymentSuccess, onPaymentError, loading, setLoading }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm 
        formData={formData}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
        loading={loading}
        setLoading={setLoading}
      />
    </Elements>
  );
};

export default StripePayment;